import React, { useState, useEffect, useRef } from "react";
import Nav from "../Nav";
import Filters from "./newfilters";
import Modal from "../Modal";
import AthleteScore from "../athlete-scores/athlete-score";
import ExportDropdown from "./ExportDropdown";
import MobileLeaderboardView from "./MobileLeaderboardView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFilter,
  faSearch,
  faChevronDown,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../services/Interceptor";
import { fetchPublicEvents } from "../../services/ContactPublicService";
import {
  fetchGraduationYears,
  fetchLeaderboardData,
} from "../../services/LeaderboardService";
import LoadingSpinner from "../LoadingSpinner";
import { toast } from "react-toastify";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { getBulkPublicTokens } from "../../services/PublicTokenService";

const positions = [
  "Guard",
  "Wide Receiver",
  "Quarterback",
  "Offensive Tackle",
  "Slot Receiver",
  "Safety",
  "Defensive End",
  "Running Back",
  "Corner",
  "Tight End",
  "Defensive Tackle",
  "Edge/Outside Linebacker",
  "Defensive Line",
];

const filters = [
  {
    label: "All",
    image: "/All.svg",
    columns: [
      "fortyYardDash",
      "benchPress",
      "verticalJump",
      "broadJump",
      "threeCone",
      "shuttle",
    ],
  },
  {
    label: "40 Yard Dash",
    image: "/40Yard.svg",
    columns: ["tenYardDash", "fortyYardDash", "twentyYardDash"],
  },
  {
    label: "Bench Press",
    image: "/BenchPress.svg",
    columns: ["benchPress"],
  },
  {
    label: "Vertical Jump",
    image: "/VerticalJump.svg",
    columns: ["verticalJump"],
  },
  { label: "Broad Jump", image: "/BroadJump.svg", columns: ["broadJump"] },
  {
    label: "Three Cone L-drill",
    image: "/ThreeCone.svg",
    columns: ["threeCone"],
  },
  {
    label: "20 Yard Shuttle",
    image: "/20Yard.svg",
    columns: ["shuttle"],
  },
];

const columnConfig = {
  rank: { label: "Position Rank", sortKey: "rank" },
  positionScore: { label: "Position Score", sortKey: "positionScore" },
  positionAthleticScore: {
    label: "Position Athletic Score",
    sortKey: "positionAthleticScore",
  },
  positionMeasurementScore: {
    label: "Position Measurement Score",
    sortKey: "positionMeasurementScore",
  },
  athleteName: { label: "Athlete Name", sortKey: "athleteName" },
  primaryPosition: { label: "Primary Position", sortKey: "primaryPosition" },
  eventsAttended: { label: "Events", sortKey: "eventsAttended" },
  graduation: { label: "Graduation", sortKey: "graduation" },
  highSchool: { label: "School", sortKey: "highSchool" },
  state: { label: "State", sortKey: "state" },
  height: { label: "Height", sortKey: "heightForSorting" },
  weight: { label: "Weight", sortKey: "weight" },
  wingspan: { label: "Wingspan", sortKey: "wingspan" },
  hand: { label: "Hand", sortKey: "hand" },
  fortyYardDash: { label: "40 Yard Dash", sortKey: "fortyYardDash" },
  shuttle: { label: "20 Yard Shuttle", sortKey: "shuttle" },
  tenYardDash: { label: "10 Yard Split", sortKey: "tenYardDash" },
  twentyYardDash: { label: "20 Yard Split", sortKey: "twentyYardDash" },
  benchPress: { label: "Bench Press", sortKey: "benchPressRank" },
  verticalJump: { label: "Vertical Jump", sortKey: "verticalJump" },
  broadJump: { label: "Broad Jump", sortKey: "broadJump" },
  threeCone: { label: "Three Cone L-drill", sortKey: "threeCone" },
};

const CONTACT_EXPORT_FILENAME = "Export with Contact Info.csv";
const PROFILE_LINK_HEADER = "Profile Link";

const contactExportColumns = [
  {
    header: "Athlete Email",
    paths: ["athleteEmail", "email", "contact.athleteEmail"],
  },
  {
    header: "Athlete Phone",
    paths: ["athletePhone", "phone", "contact.athletePhone"],
  },
  { header: "Parent Email", paths: ["parentEmail", "contact.parentEmail"] },
  { header: "Parent Phone", paths: ["parentPhone", "contact.parentPhone"] },
  { header: "X", paths: ["twitter", "x", "contact.twitter", "contact.x"] },
  { header: "Instagram", paths: ["instagram", "contact.instagram"] },
];

const getEmptyFilterParams = () => ({
  year: [],
  position: [],
  eventIds: [],
  highSchools: [],
  states: [],
  heightFilterOperator: null,
  heightValue: "",
  weightFilterOperator: null,
  weightValue: "",
  wingspanFilterOperator: null,
  wingspanValue: "",
  handFilterOperator: null,
  handValue: "",
  scoreFilterTypes: [],
  scoreFilterOperator: null,
  positionScoreFilterType: null,
  positionScoreValue: "",
});

const getFirstNonEmptyValue = (obj, paths) => {
  for (const path of paths) {
    const value = path.split(".").reduce((acc, part) => acc?.[part], obj);
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return "";
};

export default function Leaderboard() {
  const authUser = useAuthUser();
  const apiInstance = useAxiosInstance();
  const [search, setSearch] = useState("");
  const [yearDropdown, setYearDropdown] = useState(false);
  const [eventDropdown, setEventDropdown] = useState(false);
  const [filterDropdown, setFilterDropdown] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState("Select Year");
  const [selectedEvent, setSelectedEvent] = useState("Select Event");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [tempSelectedFilters, setTempSelectedFilters] = useState([]);
  const [allSelectedFilters, setAllSelectedFilters] = useState([]);
  const [sortBy, setSortBy] = useState("rank");
  const [sortDirection, setSortDirection] = useState("asc");
  const [years, setYears] = useState([]);
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [athleteId, setAthleteId] = useState(0);
  const [activeFilter, setActiveFilter] = useState("All");
  const [filterParams, setFilterParams] = useState(getEmptyFilterParams);
  const hasEventFilter =
    filterParams.eventIds && filterParams.eventIds.length > 0;

  const dropdownRefs = useRef([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const isAthlete = authUser?.user?.role === "athlete";
  const isAdmin = authUser?.user?.role === "admin";

  const SCORE_TYPE_TO_COLUMN = {
    PositionAthleticScore: "positionAthleticScore",
    PositionMeasurementScore: "positionMeasurementScore",
  };

  const getVisibleColumns = () => {
    const scoreColumn =
      SCORE_TYPE_TO_COLUMN[filterParams.positionScoreFilterType];

    const baseColumns = [
      "athleteName",
      "rank",
      "positionScore",
      ...(scoreColumn ? [scoreColumn] : []),
      "primaryPosition",
      "graduation",
      "highSchool",
      "state",
      "height",
      "weight",
      "wingspan",
      "hand",
    ];

    if (hasEventFilter) {
      const heightIdx = baseColumns.indexOf("height");
      baseColumns.splice(heightIdx !== -1 ? heightIdx : 7, 0, "eventsAttended");
    }

    // -------- ALL FILTER --------
    if (activeFilter === "All") {
      return [
        ...baseColumns,
        "tenYardDash",
        "fortyYardDash",
        "shuttle",
        "benchPress",
        "verticalJump",
        "broadJump",
        "threeCone",
      ];
    }

    // -------- SPECIFIC FILTER --------
    const filter = filters?.find((f) => f.label === activeFilter);
    if (!filter) return baseColumns;

    return [...new Set([...baseColumns, ...filter.columns])];
  };

  const visibleColumns = getVisibleColumns();

  const fetchAthletes = async () => {
    setLoading(true);
    try {
      const asNonEmptyArray = (v) =>
        v == null || v === ""
          ? undefined
          : Array.isArray(v)
            ? v.length
              ? v
              : undefined
            : [v];
      const getConditionalFilter = (operator, value) => {
        const hasOperator = operator && operator !== "Select";
        const hasValue = value !== undefined && value !== null && value !== "";

        return hasOperator && hasValue
          ? { operator, value: Number(value) }
          : { operator: undefined, value: undefined };
      };
      const heightFilter = getConditionalFilter(
        filterParams.heightFilterOperator,
        filterParams.heightValue,
      );
      const weightFilter = getConditionalFilter(
        filterParams.weightFilterOperator,
        filterParams.weightValue,
      );
      const wingspanFilter = getConditionalFilter(
        filterParams.wingspanFilterOperator,
        filterParams.wingspanValue,
      );
      const handFilter = getConditionalFilter(
        filterParams.handFilterOperator,
        filterParams.handValue,
      );
      const positionScoreFilterTypes = asNonEmptyArray(
        filterParams.positionScoreFilterType,
      );
      const hasPositionScoreFilter =
        positionScoreFilterTypes &&
        filterParams.positionScoreValue !== undefined &&
        filterParams.positionScoreValue !== null &&
        filterParams.positionScoreValue !== "";

      const params = {
        year: filterParams.year,
        position: filterParams.position,
        eventIds: filterParams.eventIds,
        highSchools: filterParams.highSchools,
        states: filterParams.states,
        heightFilterOperator: heightFilter.operator,
        heightValue: heightFilter.value,
        weightFilterOperator: weightFilter.operator,
        weightValue: weightFilter.value,
        wingspanFilterOperator: wingspanFilter.operator,
        wingspanValue: wingspanFilter.value,
        handFilterOperator: handFilter.operator,
        handValue: handFilter.value,
        scoreFilterTypes: filterParams.scoreFilterTypes,
        scoreFilterOperator: filterParams.scoreFilterOperator,
        positionScoreFilterTypes: hasPositionScoreFilter
          ? positionScoreFilterTypes
          : undefined,
        positionScoreValue: hasPositionScoreFilter
          ? Number(filterParams.positionScoreValue)
          : undefined,
      };

      const data = await fetchLeaderboardData(apiInstance, params);
      setAthletes(data || []);
    } catch (error) {
      console.error("Error fetching athletes:", error);
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAthletes();
  }, [apiInstance, filterParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !dropdownRefs.current.some((ref) => ref && ref.contains(event.target))
      ) {
        setYearDropdown(false);
        setEventDropdown(false);
        setFilterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizeStr = (str) =>
    (str ?? "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const sortedFilteredAthletes = [...athletes]
    .filter((a) => normalizeStr(a.athleteName).includes(normalizeStr(search)))
    .sort((a, b) => {
      const aIsZero =
        a[sortBy] === 0 ||
        a[sortBy] === null ||
        a[sortBy] === undefined ||
        a[sortBy] === "0 x 0" ||
        a[sortBy] === "0.000 in" ||
        a[sortBy] === "0.000 lbs" ||
        a[sortBy] === "0' 0''";
      const bIsZero =
        b[sortBy] === 0 ||
        b[sortBy] === null ||
        b[sortBy] === undefined ||
        b[sortBy] === "0 x 0" ||
        b[sortBy] === "0.000 in" ||
        b[sortBy] === "0.000 lbs" ||
        b[sortBy] === "0' 0''";

      if (aIsZero && !bIsZero) return 1;
      if (!aIsZero && bIsZero) return -1;
      if (aIsZero && bIsZero) return 0;

      if (a[sortBy] < b[sortBy]) return sortDirection === "asc" ? -1 : 1;
      if (a[sortBy] > b[sortBy]) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const resetFilters = () => {
    setSelectedYear("Select Year");
    setSelectedEvent("Select Event");
    setSelectedFilters([]);
    setTempSelectedFilters([]);
    setSearch("");
    setSortBy("rank");
    setSortDirection("asc");
    setActiveFilter("All");
    setFilterParams(getEmptyFilterParams());
    setAllSelectedFilters([]);
  };

  const clearFilter = (filterToRemove) => {
    const newSelectedFilters = allSelectedFilters.filter(
      (f) => f.value !== filterToRemove.value,
    );
    setAllSelectedFilters(newSelectedFilters);

    let updatedFilters = { ...filterParams };

    switch (filterToRemove.type) {
      case "year":
        updatedFilters.year = updatedFilters.year.filter(
          (y) => y !== parseInt(filterToRemove.original),
        );
        break;
      case "position":
        updatedFilters.position = updatedFilters.position.filter(
          (p) => p !== filterToRemove.original,
        );
        break;
      case "eventIds": {
        const originalId =
          filterToRemove.original?.value ?? filterToRemove.original?.id;
        updatedFilters.eventIds = updatedFilters.eventIds.filter((e) =>
          typeof e === "object"
            ? (e.value ?? e.id) !== originalId
            : e !== originalId,
        );
        break;
      }
      case "highSchools":
        updatedFilters.highSchools = updatedFilters.highSchools.filter(
          (s) => s !== filterToRemove.original,
        );
        break;
      case "states":
        updatedFilters.states = updatedFilters.states.filter(
          (s) =>
            s !== filterToRemove.original.split("(")[0].replace(")", "").trim(),
        );
        break;
      case "height":
        updatedFilters.heightValue = 0;
        updatedFilters.heightFilterOperator = "";
        break;
      case "weight":
        updatedFilters.weightValue = 0;
        updatedFilters.weightFilterOperator = "";
        break;
      case "wingspan":
        updatedFilters.wingspanValue = 0;
        updatedFilters.wingspanFilterOperator = "";
        break;
      case "hand":
        updatedFilters.handValue = 0;
        updatedFilters.handFilterOperator = "";
        break;
      case "score":
        updatedFilters.scoreFilterTypes = [];
        updatedFilters.positionScoreValue = 0;
        updatedFilters.scoreFilterOperator = "";
        break;
      case "positionScore" ||
        "positionAthleticScore" ||
        "positionMeasurementScore":
        updatedFilters.positionScoreFilterType = null;
        updatedFilters.positionScoreValue = 0;
        break;
      default:
        break;
    }

    setFilterParams(updatedFilters);
  };

  const clearAll = () => {
    setTempSelectedFilters([]);
    setAllSelectedFilters([]);
    setSelectedFilters([]);
    setSelectedYear("Select Year");
    setFilterParams(getEmptyFilterParams());
  };

  const handleViewClick = (athleteId) => {
    setAthleteId(athleteId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setAthleteId(0);
    setIsModalOpen(false);
  };

  const handleApplyFilters = (filters) => {
    // Transform event objects to IDs for API call
    const eventIds = filters.eventIds.map((event) =>
      typeof event === "object" ? event.value || event.id : event,
    );

    const apiFilters = {
      year: filters.year?.map((y) => parseInt(y)),
      position: filters.position,
      eventIds: eventIds,
      highSchools: filters.highSchools,
      states: filters.states?.map((s) =>
        s.split("(")[0].replace(")", "").trim(),
      ),
      heightFilterOperator: filters.heightFilterOperator,
      heightValue: parseFloat(filters.heightValue) || 0,
      weightFilterOperator: filters.weightFilterOperator,
      weightValue: parseFloat(filters.weightValue) || 0,
      wingspanFilterOperator: filters.wingspanFilterOperator,
      wingspanValue: parseFloat(filters.wingspanValue) || 0,
      handFilterOperator: filters.handFilterOperator,
      handValue: parseFloat(filters.handValue) || 0,
      scoreFilterTypes: filters.scoreFilterTypes.map((st) => st.label),
      scoreFilterOperator: filters.scoreFilterOperator,
      positionScoreFilterType: filters.positionScoreType || null,
      positionScoreValue: parseFloat(filters.positionScoreValue) || 0,
    };

    setFilterParams(apiFilters);

    // Create selected filters display
    const allSelected = [
      ...filters.year.map((y) => ({
        type: "year",
        value: `Year: ${y}`,
        original: y,
      })),
      ...filters.position.map((p) => ({
        type: "position",
        value: `Position: ${p}`,
        original: p,
      })),
      ...filters.eventIds.map((e) => {
        const eventName =
          typeof e === "object" ? e.label || e.name : `Event ${e}`;
        const eventDate = typeof e === "object" ? e.date : "";
        return {
          type: "eventIds",
          value: `Event: ${eventDate ? `${eventDate} ` : ""}${eventName}`,
          original: typeof e === "object" ? e : { id: e, name: eventName },
        };
      }),
      ...filters.highSchools.map((s) => ({
        type: "highSchools",
        value: `School: ${s}`,
        original: s,
      })),
      ...filters.states.map((s) => ({
        type: "states",
        value: `State: ${s.split("(")[0].replace(")", "").trim()}`,
        original: s,
      })),
      ...(filters.heightValue
        ? [
            {
              type: "height",
              value: `Height: ${filters.heightFilterOperator} ${filters.heightValue} inches`,
              original: filters.heightValue,
              operator: filters.heightFilterOperator,
            },
          ]
        : []),
      ...(filters.weightValue
        ? [
            {
              type: "weight",
              value: `Weight: ${filters.weightFilterOperator} ${filters.weightValue} lbs`,
              original: filters.weightValue,
              operator: filters.weightFilterOperator,
            },
          ]
        : []),
      ...(filters.wingspanValue
        ? [
            {
              type: "wingspan",
              value: `Wingspan: ${filters.wingspanFilterOperator} ${filters.wingspanValue} inches`,
              original: filters.wingspanValue,
              operator: filters.wingspanFilterOperator,
            },
          ]
        : []),
      ...(filters.handValue
        ? [
            {
              type: "hand",
              value: `Hand: ${filters.handFilterOperator} ${filters.handValue} inches`,
              original: filters.handValue,
              operator: filters.handFilterOperator,
            },
          ]
        : []),
      ...("positionScoreType" in filters && filters.positionScoreType !== null
        ? [
            {
              type: "score",
              value: `Score: ${filters.positionScoreType} ${filters.positionScoreValue}`,
              original: filters.positionScoreType,
              operator: filters.scoreFilterOperator,
              scoreValue: filters.positionScoreValue,
            },
          ]
        : []),
    ];

    setAllSelectedFilters(allSelected);
  };

  const renderSortableHeader = (label, key, headerKey) => {
    const isActive = sortBy === key;
    let icon = faSort;
    if (isActive) icon = sortDirection === "asc" ? faSortUp : faSortDown;

    return (
      <th
        key={headerKey}
        className="p-2 cursor-pointer select-none"
        onClick={() => {
          if (sortBy === key) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
          } else {
            setSortBy(key);
            setSortDirection("asc");
          }
        }}
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          {label}
          <FontAwesomeIcon icon={icon} className="text-xs text-gray-600" />
        </div>
      </th>
    );
  };

  const sanitizeCsvValue = (value, emptyFallback = "N/A") => {
    const normalizedValue =
      value === null || value === undefined || value === ""
        ? emptyFallback
        : value;

    return `"${String(normalizedValue).replace(/"/g, '""')}"`;
  };

  const downloadCsv = (filename, headers, rows, emptyFallback = "N/A") => {
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((value) => sanitizeCsvValue(value, emptyFallback)).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const buildContactExportRow = (athlete) => {
    return contactExportColumns.map(({ paths }) =>
      getFirstNonEmptyValue(athlete, paths),
    );
  };

  const getLeaderboardExportCellValue = (athlete, column) => {
    if (column === "height") {
      return `${athlete.heightFt ?? 0}' ${athlete.heightIn ?? 0}''`;
    }

    if (column === "wingspan") {
      if (!athlete.wingspan) return "";
      const wingspanFt = Math.floor(athlete.wingspan / 12);
      const wingspanIn = parseFloat((athlete.wingspan % 12).toFixed(2));
      return `${wingspanFt}' ${wingspanIn}''`;
    }

    if (column === "hand") return athlete.hand ? `${athlete.hand}''` : "";

    if (column === "tenYardDash") return athlete.tenYardDash || "";
    if (column === "twentyYardDash") return athlete.twentyYardDash || "";

    return athlete[column] || "";
  };

  const buildProfileLinkValue = (token) =>
    token ? `${window.location.origin}/public-link/${token}` : "";

  const fetchProfileLinkMap = async (athletes) => {
    const userIds = [
      ...new Set(athletes.map((athlete) => athlete.userId).filter(Boolean)),
    ];
    if (!userIds.length) return {};

    try {
      const data = await getBulkPublicTokens(apiInstance, userIds);
      return data?.tokens ?? data ?? {};
    } catch (error) {
      console.error("Error fetching public tokens:", error);
      toast.error("Failed to generate profile links.");
      return {};
    }
  };

  const exportLeaderboardCsv = async () => {
    if (!sortedFilteredAthletes.length) {
      toast.warning("No athletes available to export.");
      return;
    }

    const tokenMap = isAdmin
      ? await fetchProfileLinkMap(sortedFilteredAthletes)
      : null;

    const headers = visibleColumns.map((column) => columnConfig[column].label);
    if (isAdmin) headers.push(PROFILE_LINK_HEADER);

    const rows = sortedFilteredAthletes.map((athlete) => {
      const row = visibleColumns.map((column) =>
        getLeaderboardExportCellValue(athlete, column),
      );
      if (isAdmin) row.push(buildProfileLinkValue(tokenMap[athlete.userId]));
      return row;
    });

    downloadCsv("Export leaderboard.csv", headers, rows, "");
  };

  const exportContactInfoCsv = async () => {
    if (!sortedFilteredAthletes.length) {
      toast.warning("No athletes available to export.");
      return;
    }

    try {
      const tokenMap = isAdmin
        ? await fetchProfileLinkMap(sortedFilteredAthletes)
        : null;

      const exportedRows = sortedFilteredAthletes.map((athlete) => {
        const row = [
          ...visibleColumns.map((column) =>
            getLeaderboardExportCellValue(athlete, column),
          ),
          ...buildContactExportRow(athlete),
        ];
        if (isAdmin) row.push(buildProfileLinkValue(tokenMap[athlete.userId]));
        return row;
      });

      const hasAnyContactValue = exportedRows.some((row) =>
        row
          .slice(
            visibleColumns.length,
            visibleColumns.length + contactExportColumns.length,
          )
          .some((value) => value !== ""),
      );

      if (!hasAnyContactValue) {
        toast.warning(
          "No contact fields found in the enhanced leaderboard data.",
        );
        return;
      }

      const headers = [
        ...visibleColumns.map((column) => columnConfig[column].label),
        ...contactExportColumns.map(({ header }) => header),
        ...(isAdmin ? [PROFILE_LINK_HEADER] : []),
      ];

      downloadCsv(CONTACT_EXPORT_FILENAME, headers, exportedRows, "");

      toast.success("Contact info exported successfully.");
    } catch (error) {
      console.error("Error exporting contact info:", error);
      toast.error("Failed to export contact info.");
    }
  };

  return (
    <>
      <Nav />

      {/* ── Mobile view (< 768 px) ── */}
      <div className="md:hidden bg-white min-h-screen px-4 py-4">
        <MobileLeaderboardView
          activeFilter={activeFilter}
          canExportContactInfo={!isAthlete}
          columnFilters={filters}
          disabledExport={!sortedFilteredAthletes.length}
          filterParams={filterParams}
          handleViewClick={handleViewClick}
          includeGraduatedKids={false}
          isExportingContactInfo={false}
          isHighSchoolCoach={false}
          isHighSchoolLeaderboard={false}
          loading={loading}
          onApplyFilters={handleApplyFilters}
          onClearFilters={resetFilters}
          onExport={exportLeaderboardCsv}
          onExportContactInfo={exportContactInfoCsv}
          search={search}
          setActiveFilter={setActiveFilter}
          setIncludeGraduatedKids={() => {}}
          setSearch={setSearch}
          setSortBy={setSortBy}
          setSortDirection={setSortDirection}
          sortBy={sortBy}
          sortDirection={sortDirection}
          sortedFilteredAthletes={sortedFilteredAthletes}
        />
      </div>

      {/* ── Desktop view (≥ 768 px) ── */}
      <div className="hidden md:block py-10 px-4">
        <div className="text-center">
          <div className="text-3xl text-white uppercase pb-2">Leaderboard</div>
          <p className="text-lg text-white">
            Compare top-performing athletes across all events
          </p>
        </div>
        <div className="max-w-8xl bg-white rounded-xl mx-0 lg:mx-16 mt-5 p-2 lg:p-4 text-sm">
          {/* Top Year Dropdown */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="">
              <Filters
                onApplyFilters={handleApplyFilters}
                onClearFilters={resetFilters}
                filterParams={filterParams}
              />
              <div className="flex gap-4">
                {/* Year Dropdown */}
                <div
                  className="relative hidden"
                  ref={(el) => (dropdownRefs.current[0] = el)}
                >
                  <button
                    onClick={async () => {
                      let yearsLst = await fetchGraduationYears(apiInstance);
                      setYears(yearsLst);
                      setYearDropdown(!yearDropdown);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-2 flex items-center gap-2 "
                  >
                    {selectedYear} <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  {yearDropdown && (
                    <ul className="absolute z-10 mt-1 bg-white border rounded-md shadow-lg w-40">
                      {years.map((year) => (
                        <li
                          key={year}
                          onClick={async () => {
                            setSelectedYear(year);
                            setYearDropdown(false);
                            setAllSelectedFilters([
                              year,
                              ...tempSelectedFilters,
                            ]);
                          }}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                          {year}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {/* Event Dropdown */}
                <div
                  className="relative hidden"
                  ref={(el) => (dropdownRefs.current[1] = el)}
                >
                  <button
                    onClick={async () => {
                      let eventsLst = await fetchPublicEvents(apiInstance);
                      setEvents(eventsLst);
                      setEventDropdown(!eventDropdown);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-2 flex items-center gap-2 text-sm cursor-pointer"
                  >
                    {selectedEvent} <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  {eventDropdown && (
                    <ul className="absolute z-10 mt-1 bg-white border rounded-md shadow-lg w-64 max-h-60 overflow-y-auto">
                      {events.map((event) => (
                        <li
                          key={event.id}
                          onClick={async () => {
                            setSelectedEvent(event.name);
                            setEventDropdown(false);
                          }}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                          {event.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="mb-0">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className=" text-right">
                  {/* Export Button */}
                  <div className="flex flex-wrap justify-end gap-4">
                    {isAthlete ? (
                      <button
                        className={`px-4 py-2 rounded-lg text-sm ${
                          !sortedFilteredAthletes.length
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gray-200 cursor-pointer"
                        }`}
                        onClick={exportLeaderboardCsv}
                        disabled={!sortedFilteredAthletes.length}
                      >
                        <FontAwesomeIcon icon={faDownload} className="pr-2" />
                        Export
                      </button>
                    ) : (
                      <ExportDropdown
                        disabled={!sortedFilteredAthletes.length}
                        actions={[
                          { label: "Export", onClick: exportLeaderboardCsv },
                          {
                            label: "Export with Contact Info",
                            onClick: exportContactInfoCsv,
                          },
                        ]}
                      />
                    )}
                  </div>
                  {/* Filter Dropdown */}
                  <div
                    className="relative hidden"
                    ref={(el) => (dropdownRefs.current[2] = el)}
                  >
                    <button
                      onClick={() => {
                        setTempSelectedFilters([...selectedFilters]);
                        setFilterSearch("");
                        setFilterDropdown(!filterDropdown);
                      }}
                      className="bg-gray-200 px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
                    >
                      <FontAwesomeIcon icon={faFilter} /> Filter by position
                    </button>
                    {filterDropdown && (
                      <div className="absolute right-0 mt-2 bg-white border rounded-md shadow-lg p-4 z-20 w-72 max-h-80 flex flex-col">
                        <input
                          type="text"
                          placeholder="Search positions..."
                          value={filterSearch}
                          onChange={(e) => setFilterSearch(e.target.value)}
                          className="mb-3 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                        <div className="overflow-y-auto flex-1">
                          {positions
                            .filter((pos) =>
                              pos
                                .toLowerCase()
                                .includes(filterSearch.toLowerCase()),
                            )
                            .map((position) => (
                              <label
                                key={position}
                                className="flex items-center space-x-2 mb-2"
                              >
                                <input
                                  className="w-4 h-4"
                                  type="checkbox"
                                  checked={tempSelectedFilters.includes(
                                    position,
                                  )}
                                  onChange={() => {
                                    setTempSelectedFilters((prev) =>
                                      prev.includes(position)
                                        ? prev.filter((p) => p !== position)
                                        : [...prev, position],
                                    );
                                    setAllSelectedFilters((prev) =>
                                      prev.includes(position)
                                        ? prev.filter((p) => p !== position)
                                        : [...prev, position],
                                    );
                                  }}
                                />
                                <span>{position}</span>
                              </label>
                            ))}
                        </div>
                        <div className="mt-3 flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setTempSelectedFilters([...selectedFilters]);
                              setFilterDropdown(false);
                            }}
                            className="px-3 py-1 border border-gray-400 rounded-md text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={async () => {
                              setSelectedFilters(tempSelectedFilters);
                              setFilterDropdown(false);
                            }}
                            className="px-3 py-1 bg-red-500 text-white rounded-md text-sm"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="">
                  <div className="flex gap-4 items-center">
                    {/* Search input */}
                    <div className="relative">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="absolute top-3 left-3 text-gray-400 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Search player name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    {/* Clear filters */}
                    <button
                      onClick={resetFilters}
                      className="text-sm text-blue-600 hover:underline px-2 py-1 border border-blue-500 rounded-md"
                    >
                      Clear All Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Filters */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 justify-between items-center">
            <div className="">
              <span className="text-md font-medium">Filters:</span>
              <div
                className="flex items-center space-x-2 mb-4"
                role="region"
                aria-label="Selected filters"
              >
                <div className="flex flex-wrap">
                  {allSelectedFilters.length > 0 ? (
                    allSelectedFilters.map((filter, index) => (
                      <div
                        key={index}
                        className=" bg-gray-200 text-gray-700 rounded-full px-3 py-1 text-sm mb-1 mr-1"
                      >
                        {filter.value}
                        <button
                          onClick={() => clearFilter(filter)}
                          aria-label={`Remove filter ${filter.value}`}
                          className="ml-2 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-500 italic">
                      No filters selected
                    </span>
                  )}
                  {allSelectedFilters.length > 0 && (
                    <button
                      onClick={clearAll}
                      className="ml-4 text-sm text-red-600 hover:underline focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <div className="grid gap-2">
                <div className="">
                  <span className="text-md font-medium">Columns Filters:</span>
                </div>
                <div className="w-full  overflow-auto">
                  <div className="flex border overflow-auto rounded-lg shadow-sm divide-x divide-gray-300 justify-between">
                    {filters.map((item, index) => {
                      const isActive = item.label === activeFilter;
                      const baseClasses =
                        "flex flex-col items-center px-3 py-2 text-sm cursor-pointer text-center";
                      return (
                        <div
                          key={index}
                          onClick={() => setActiveFilter(item.label)}
                          className={`${baseClasses} ${
                            isActive
                              ? "bg-blue-50 text-blue-600"
                              : "bg-white text-gray-800 hover:bg-gray-100"
                          }`}
                        >
                          <img
                            src={item.image}
                            alt={item.label}
                            className="h-10 mb-1"
                          />
                          <span className="leading-tight break-words">
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Table */}
          <div className="overflow-auto max-h-[80vh] w-full">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-red-100 text-gray-800">
                <tr className="sticky top-0 z-10 bg-red-100">
                  <th className="p-2 text-center">#</th>
                  {visibleColumns.map(
                    (column) =>
                      columnConfig[column] &&
                      renderSortableHeader(
                        columnConfig[column].label,
                        columnConfig[column].sortKey,
                        column,
                      ),
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={visibleColumns.length + 1}
                      className="p-4 text-center"
                    >
                      <div className="flex justify-center py-6">
                        <LoadingSpinner />
                      </div>
                    </td>
                  </tr>
                ) : sortedFilteredAthletes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleColumns.length + 1}
                      className="p-6 text-center"
                    >
                      <div className="flex flex-col items-center justify-center text-gray-600">
                        <img
                          className="h-10 md:h-10 xl:h-16 lg:h-16 mb-4"
                          alt="No Result Found"
                          src="/no-data.png"
                        />
                        <h1 className="text-xl font-semibold text-gray-800 mb-1">
                          No Result Found
                        </h1>
                        <p className="text-gray-600 text-base">
                          Try adjusting the filters to find what you're looking
                          for.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedFilteredAthletes.map((athlete, index) => (
                    <tr
                      key={index}
                      className="border-b text-gray-600 hover:bg-red-50 cursor-pointer"
                    >
                      <td className="p-2 text-center">{index + 1}</td>
                      {visibleColumns.map((column) => {
                        if (column === "rank") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.rank}
                            </td>
                          );
                        } else if (column === "positionScore") {
                          return (
                            <td
                              key={column}
                              className="p-2 text-gray-800 font-semibold"
                              onClick={() => handleViewClick(athlete.userId)}
                            >
                              {Math.round(athlete.positionScore)}
                            </td>
                          );
                        } else if (column === "positionAthleticScore") {
                          return (
                            <td
                              key={column}
                              className="p-2 text-gray-800 font-semibold"
                              onClick={() => handleViewClick(athlete.userId)}
                            >
                              {Math.round(athlete.positionAthleticScore)}
                            </td>
                          );
                        } else if (column === "positionMeasurementScore") {
                          return (
                            <td
                              key={column}
                              className="p-2 text-gray-800 font-semibold"
                              onClick={() => handleViewClick(athlete.userId)}
                            >
                              {Math.round(athlete.positionMeasurementScore)}
                            </td>
                          );
                        } else if (column === "athleteName") {
                          return (
                            <td
                              key={column}
                              className="p-2 text-gray-800 font-semibold hover:underline hover:text-blue-700"
                              onClick={() => handleViewClick(athlete.userId)}
                            >
                              {athlete.athleteName}
                            </td>
                          );
                        } else if (column === "primaryPosition") {
                          return (
                            <td key={column} className="p-2 text-left">
                              {athlete.primaryPosition}
                            </td>
                          );
                        } else if (column === "eventsAttended") {
                          return (
                            <td key={column} className="p-2 text-left">
                              <span className="table-truncate">
                                {athlete.eventsAttended}
                              </span>
                            </td>
                          );
                        } else if (column === "graduation") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.graduation}
                            </td>
                          );
                        } else if (column === "highSchool") {
                          return (
                            <td key={column} className="p-2 text-left">
                              {athlete.highSchool}
                            </td>
                          );
                        } else if (column === "state") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.state}
                            </td>
                          );
                        } else if (column === "height") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.heightFt ?? 0}' {athlete.heightIn ?? 0}''
                            </td>
                          );
                        } else if (column === "weight") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.weight} lbs
                            </td>
                          );
                        } else if (column === "wingspan") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.wingspan
                                ? `${Math.floor(athlete.wingspan / 12)}' ${parseFloat((athlete.wingspan % 12).toFixed(2))}''`
                                : "-"}
                            </td>
                          );
                        } else if (column === "hand") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.hand ? `${athlete.hand}''` : "-"}
                            </td>
                          );
                        } else if (column === "fortyYardDash") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.fortyYardDash}
                            </td>
                          );
                        } else if (column === "tenYardDash") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.tenYardDash ?? ""}
                            </td>
                          );
                        } else if (column === "twentyYardDash") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.twentyYardDash || ""}
                            </td>
                          );
                        } else if (column === "benchPress") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.benchPress}
                            </td>
                          );
                        } else if (column === "verticalJump") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.verticalJump}
                            </td>
                          );
                        } else if (column === "broadJump") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.broadJump}
                            </td>
                          );
                        } else if (column === "threeCone") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.threeCone}
                            </td>
                          );
                        } else if (column === "shuttle") {
                          return (
                            <td key={column} className="p-2 text-center">
                              {athlete.shuttle}
                            </td>
                          );
                        }
                        return null;
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <AthleteScore
          userId={athleteId}
          showOffers={true}
          showTrackittScore={true}
          showPositionScore={true}
          searchEvent={false}
          showProfile={true}
          showRank={true}
          showLikes={true}
        />
      </Modal>
    </>
  );
}
